import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { FORM_LANGUAGES, type AssistantLanguage, type AssistantSuggestion } from '@chat-emp/shared';

import { BotIcon } from '../components/BotIcon.js';
import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { LANGUAGE_LABEL } from '../data/forms.js';
import type { ApplicationStatus } from '../fixtures/domain.js';
import {
  actionLook,
  CARD_ACTIONS,
  visibleActions,
  type ActionLook,
  type CardAction,
} from './assistantActions.js';
import styles from './AssistantCard.module.css';
import { useAssistantShortcuts } from './useAssistantShortcuts.js';
import { useClampedText } from './useClampedText.js';

interface AssistantCardProps {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /**
   * A shortcut was pressed. Absent while the card has no rows to act on — the
   * card itself will not listen when collapsed, but only the caller knows
   * whether a reading has landed.
   */
  onShortcut?: (digit: number) => void;
  /**
   * Whether a model is behind this card at all.
   *
   * Names it, and does so from a fact known before any request rather than from
   * what came back. Unassisted the card is the employer's own controls with a
   * set of openers under them — nothing has read anything, so it claims nothing:
   * no bot mark, no Beta, and no section heading over the only list inside.
   */
  assisted?: boolean;
  /**
   * A reading is in flight, so the card wears the thinking treatment.
   *
   * The card is told rather than inferring it from its children: the waiting
   * line is one child among several, and the ring belongs to the whole card.
   */
  reading?: boolean;
  /**
   * The conversation's stored verdict: the assistant recommends a decision here.
   *
   * The header is the only place this is drawn. It was the inbox row's dot too,
   * until that turned out to be readable by the candidate it judged; the row's
   * copy went, this one stayed, because the reading is fetched by the employer's
   * own session.
   *
   * Distinct from the live reading's own mark, which is gated on `reading` — the
   * flag is the stored judgement and stands until a landed reading replaces it,
   * so the header does not blink off for the length of every re-read.
   *
   * Fed from `flagged` on the reading response, which is employer-scoped. It was
   * read off the channel's own `assistant_flag` until that turned out to reach
   * every channel member — the candidate included — and the row's dot went with
   * the reader. This header is the only place the verdict is drawn now.
   */
  flagged?: boolean;
  /**
   * What the live reading would have the employer do, or null when it named
   * nothing.
   *
   * The card needs it for one thing only — whether to light the dot — so it is
   * the verdict rather than the whole reading. `Moves` is told separately,
   * because where the mark sits is that section's business.
   */
  suggest?: AssistantSuggestion | null;
  /**
   * The card is on screen but nothing on it may be pressed — a send in flight, a
   * stage being written. Distinct from `reading`, which takes the rows away
   * entirely: busy is the same card, momentarily inert.
   */
  disabled?: boolean;
  /**
   * The assistant's account of where this stands — "What I think". Its own slot
   * because the shut card shows it when there is no preview.
   */
  account?: ReactNode;
  /** What the shut card shows in place of the account — the reply preview. */
  preview?: ReactNode;
  children?: ReactNode;
  /**
   * Draw the widget's shape instead of the page's.
   *
   * 400px cannot hold the account, every action row and the drafts and stay
   * worth reading. What it cannot hold it points at rather than shrinks — see
   * `CompactShape`.
   */
  compact?: CompactShape;
}

/**
 * The widget's shape, and the facts its one body needs.
 *
 * Grouped rather than spread across seven optional props, because they travel
 * together or not at all and the type is what says so.
 *
 * The page's card takes its body as `children` since callers vary it: assisted
 * and unassisted draw different lists under different names. Compact has exactly
 * one body it can draw, so it owns it rather than asking a caller to compose the
 * only composition there is.
 */
export interface CompactShape {
  jobBounded: boolean;
  status: ApplicationStatus;
  /** The reading's drafts, drawn under the recommended row as the page draws them. */
  replies: string[];
  recommended: number | null;
  translation?: DraftTranslation;
  onTake: (action: CardAction) => void;
  onPick: (text: string, index: number) => void;
  onWriteOwn: () => void;
  /** Where "See full analysis and all actions" goes. */
  onOpenPage: () => void;
}

interface SectionProps {
  /** The small muted caption above a section — "What I think", "Actions". */
  title?: string;
  children: ReactNode;
}

/**
 * A section of the card: a caption and what sits under it.
 *
 * Collapsing this into the card would mean the card knowing about every section
 * there is; as a piece the later sub-issues each add one without touching the
 * frame.
 */
function Section({ title, children }: SectionProps) {
  return (
    <div className={styles.section}>
      {title != null && <p className={styles.sectionTitle}>{title}</p>}
      {children}
    </div>
  );
}

interface IntroProps {
  text: string;
  expanded: boolean;
  /** "Read more" on the shut card: ask the card to open. */
  onExpand: () => void;
}

/**
 * What the assistant makes of the conversation, cut to two lines.
 *
 * Rendered as text, never as markup: asterisks a model produced are shown as typed.
 */
function Intro({ text, expanded, onExpand }: IntroProps) {
  // Keyed on the text, so a new reading lands cut again.
  const [openFor, setOpenFor] = useState<string | null>(null);
  const open = expanded && openFor === text;
  const clamped = useClampedText(text, open, 2, !expanded);

  if (!text) return null;

  return (
    <Section title="What I think">
      <p className={styles.intro}>
        {clamped.text}
        {!open && (clamped.truncated || !expanded) && (
          <>
            {clamped.truncated ? '… ' : ' '}
            <button
              className={styles.more}
              onClick={() => {
                setOpenFor(text);
                if (!expanded) onExpand();
              }}
            >
              Read more
            </button>
          </>
        )}
      </p>
      <p className={styles.probe} aria-hidden ref={clamped.probeRef} />
    </Section>
  );
}

interface RowProps {
  action: CardAction;
  jobBounded: boolean;
  status: ApplicationStatus;
  onTake: (action: CardAction) => void;
  /** Drawn filled: this is the move the assistant would make. */
  suggested?: boolean;
  /** This row's place in the arrival. See `arrivalDelay`. */
  arrivalIndex?: number;
  disabled?: boolean;
}

/**
 * One move, as a row: a glyph tile, what it is called, and the key it answers to.
 *
 * A stage already reached is drawn filled and inert — it says where the
 * application is, not something to do — and takes no shortcut, because there is
 * nothing left for the key to do.
 */
function Row({ action, jobBounded, status, onTake, suggested, arrivalIndex, disabled }: RowProps) {
  const look = actionLook(action, jobBounded, status);
  const filled = suggested === true || look.done;

  return (
    <button
      style={arrivalDelay(arrivalIndex ?? 0)}
      className={[
        styles.row,
        look.tone === 'danger' ? styles.danger : styles.accent,
        filled ? styles.filled : '',
        look.done ? styles.done : '',
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={look.done || disabled}
      onClick={() => onTake(action)}
    >
      <span className={styles.rowInner}>
        <span className={styles.tile}>
          <Icon name={look.icon} size={16} filled={look.done} />
        </span>
        <span className={styles.rowLabel}>{look.label}</span>
        {suggested && <span className={styles.pill}>Recommended</span>}
      </span>
    </button>
  );
}

/**
 * Drafts start after the four actions, so the keys never shift under an employer.
 *
 * The send row itself takes none. It used to hold this number, back when it and
 * the drafts were alternatives and only one of them was ever on screen; they are
 * one group now, and the two claims on ⇧5 would have collided. The drafts keep
 * it, because "the drafts start at ⇧5" is the invariant the key layer and the
 * view both already state — and writing your own message is the one move here
 * that the composer below is always ready for anyway.
 */
const FIRST_DRAFT_SHORTCUT = CARD_ACTIONS.length + 1;

/**
 * A row's place in the arrival, handed to the stylesheet as a number.
 *
 * The stagger is a delay per row and the delay is a function of position, so the
 * only thing the markup has to know is which position this is. Counted down the
 * whole column rather than per group — the send row and the drafts under it are
 * one list to the eye, and two sequences overlapping would read as two things
 * arriving rather than one list settling.
 */
function arrivalDelay(index: number): CSSProperties {
  return { '--arrival-index': index } as CSSProperties;
}

/**
 * Whether this draft is the one the assistant recommended.
 *
 * Exported because the card draws the pill from it and the analytics report
 * against it, and those two answers have to be the same one: the row that says
 * an employer took the recommendation is only worth anything if it means the
 * draft they were actually shown the pill on. They were two copies of the same
 * expression, which is how they would drift.
 *
 * A lone draft is never recommended however the answer came back — a pill with
 * nothing to compare against says nothing.
 */
export function isRecommendedDraft(
  recommended: number | null,
  index: number,
  total: number,
): boolean {
  return recommended !== null && recommended === index && total > 1;
}

/** Each draft's own language switch. */
export interface DraftTranslation {
  /** Per draft: the language it is shown in. */
  languages: AssistantLanguage[];
  /** The draft whose translation is on its way, if any. */
  pending: number | null;
  /** The draft whose last translation failed, if any. */
  failed: number | null;
  onTranslate: (index: number, language: AssistantLanguage) => void;
}

interface DraftsProps {
  replies: string[];
  recommended: number | null;
  translation?: DraftTranslation;
  loading?: boolean;
  disabled?: boolean;
  onPick: (text: string, index: number) => void;
  /** Where the first draft falls in the card's arrival sequence. */
  firstArrival: number;
}

/**
 * The reading's drafts, wherever they are drawn.
 *
 * One renderer for both shapes, so the quote rule, the Recommended mark and the
 * copy glyph cannot come to mean one thing on the page and another in the
 * widget. The only thing a shape gets to decide is where its drafts fall in the
 * arrival — the page counts them after its action rows, the widget after the one
 * row it drew.
 */
function Drafts({
  replies,
  recommended,
  translation,
  loading,
  disabled,
  onPick,
  firstArrival,
}: DraftsProps) {
  if (loading !== true && replies.length === 0) return null;

  return (
    <div className={styles.drafts}>
      {loading === true ? (
        <span className={styles.draftSkeleton} />
      ) : (
        replies.map((reply, index) => {
          const marked = isRecommendedDraft(recommended, index, replies.length);
          const shownIn = translation?.languages[index];
          const pending = translation?.pending === index;
          return (
            <div
              key={reply}
              className={marked ? `${styles.draft} ${styles.draftSuggested}` : styles.draft}
              style={arrivalDelay(firstArrival + index)}
              aria-busy={pending || undefined}
            >
              <span className={styles.draftKey}>
                <Icon name="copy" size={15} />
              </span>
              {(marked || translation) && (
                <span className={styles.draftHead}>
                  {marked && <span className={styles.pill}>Recommended</span>}
                  {translation &&
                    FORM_LANGUAGES.filter((l) => l !== shownIn).map((language) => (
                      <Button
                        key={language}
                        variant="chip"
                        aria-label={`Translate draft ${index + 1} to ${LANGUAGE_LABEL[language].name}`}
                        disabled={disabled || translation.pending !== null}
                        onClick={() => translation.onTranslate(index, language)}
                      >
                        {LANGUAGE_LABEL[language].short}
                      </Button>
                    ))}
                </span>
              )}
              <button
                className={styles.draftPick}
                disabled={disabled || pending}
                onClick={() => onPick(reply, index)}
              >
                <span
                  className={
                    pending ? `${styles.draftText} ${styles.draftPending}` : styles.draftText
                  }
                >
                  {reply}
                </span>
              </button>
              {translation?.failed === index && (
                <span className={styles.translateError} role="alert">
                  Couldn&apos;t translate. Try again.
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

interface SendRowProps {
  onWriteOwn: () => void;
  /** The draft under it is the recommended one: carry the pill on the row. */
  recommended?: boolean;
  arrivalIndex: number;
  disabled?: boolean;
}

function SendRow({ onWriteOwn, recommended, arrivalIndex, disabled }: SendRowProps) {
  return (
    <button
      className={`${styles.row} ${styles.accent}`}
      style={arrivalDelay(arrivalIndex)}
      disabled={disabled}
      onClick={onWriteOwn}
    >
      <span className={styles.rowInner}>
        <span className={styles.tile}>
          <Icon name="chatBubble" size={16} />
        </span>
        <span className={styles.rowLabel}>Send a message</span>
        {recommended && <span className={styles.pill}>Recommended</span>}
      </span>
    </button>
  );
}

function DraftPreview({ text, onExpand }: { text: string; onExpand: () => void }) {
  // Always offered: shut, the rest of the card is behind it even when the draft fits.
  const clamped = useClampedText(text, false, 2, true);

  return (
    <div className={styles.preview}>
      <p className={styles.previewText}>
        {clamped.text}
        {clamped.truncated ? '… ' : ' '}
        <button className={styles.more} onClick={onExpand}>
          Read more
        </button>
      </p>
      <p className={styles.previewProbe} aria-hidden ref={clamped.probeRef} />
    </div>
  );
}

interface PreviewProps {
  replies: string[];
  recommended: number | null;
  onWriteOwn: () => void;
  onExpand: () => void;
  disabled?: boolean;
}

/** The shut card's reply group: the recommended draft (or the first), cut to two lines. */
function Preview({ replies, recommended, onWriteOwn, onExpand, disabled }: PreviewProps) {
  const shown = recommended !== null && replies[recommended] !== undefined ? recommended : 0;
  const text = replies[shown];
  if (text === undefined) return null;

  return (
    <div className={styles.replyGroup}>
      <SendRow
        onWriteOwn={onWriteOwn}
        recommended={isRecommendedDraft(recommended, shown, replies.length)}
        arrivalIndex={0}
        disabled={disabled}
      />
      <DraftPreview text={text} onExpand={onExpand} />
    </div>
  );
}

/**
 * The one action the compact card draws, or null.
 *
 * Null covers three cases that arrive differently and read the same at 400px:
 * the reading named no decision, it named one this conversation does not offer,
 * or it named the stage the application already has. The last is the one worth
 * spelling out — a row offering what is already true is a press that changes
 * nothing, and `ActionLook.done` is how the page says so too.
 */
function compactStep(
  suggest: AssistantSuggestion | null,
  jobBounded: boolean,
  status: ApplicationStatus,
): { action: CardAction; look: ActionLook } | null {
  if (suggest === null || suggest === 'message') return null;
  if (!visibleActions(jobBounded, status).includes(suggest)) return null;

  const look = actionLook(suggest, jobBounded, status);
  return look.done ? null : { action: suggest, look };
}

interface MovesProps {
  jobBounded: boolean;
  status: ApplicationStatus;
  /**
   * What the reading would have the employer press, or null when it named
   * nothing. It decides where the mark sits and nothing else — never which rows
   * are drawn.
   */
  suggest: AssistantSuggestion | null;
  replies: string[];
  /**
   * Which draft the assistant named, or null when it named none.
   *
   * Not defaulted to the first: the pill is a claim about a specific draft, and
   * the model usually offers alternatives rather than a ranking.
   */
  recommended: number | null;
  /**
   * The drafts are still coming. Drawn as a shimmer in their place: a draft is
   * replaced by a block of text of no knowable length, and there is no honest
   * phrase to put in a box whose size is the only thing being promised.
   */
  repliesLoading?: boolean;
  translation?: DraftTranslation;
  onTake: (action: CardAction) => void;
  onPick: (text: string, index: number) => void;
  /** Pressing the send row itself: hand the composer the caret to write your own. */
  onWriteOwn: () => void;
  /** See `assisted` on the card: decides whether the list is named. */
  assisted?: boolean;
  disabled?: boolean;
}

/**
 * The reply group, then every move the conversation allows, with the assistant's
 * choice marked — on a row, on a draft, or nowhere when the reading named nothing.
 */
function Moves({
  jobBounded,
  status,
  suggest,
  replies,
  recommended,
  repliesLoading,
  translation,
  onTake,
  onPick,
  onWriteOwn,
  assisted = true,
  disabled,
}: MovesProps) {
  // Shared with the key layer, so what is drawn and what a key may take cannot
  // drift apart.
  const offered = visibleActions(jobBounded, status);
  // Only a decision marks a row; 'message' marks a draft instead, and null marks
  // nothing.
  const decided = suggest !== null && suggest !== 'message' ? suggest : null;
  const firstRow = 1 + (repliesLoading === true ? 1 : replies.length);

  return (
    <>
      <div className={styles.section}>
        <div className={styles.replyGroup}>
          <SendRow onWriteOwn={onWriteOwn} arrivalIndex={0} disabled={disabled} />
          <Drafts
            replies={replies}
            recommended={recommended}
            translation={translation}
            loading={repliesLoading}
            disabled={disabled}
            onPick={onPick}
            firstArrival={1}
          />
        </div>
      </div>
      {/* Untitled on the unassisted card: its header already says "Quick Actions". */}
      <Section title={assisted ? 'Actions' : undefined}>
        <div className={styles.rows}>
          {offered.map((action, index) => (
            <Row
              key={action}
              action={action}
              jobBounded={jobBounded}
              status={status}
              onTake={onTake}
              suggested={action === decided}
              disabled={disabled}
              arrivalIndex={firstRow + index}
            />
          ))}
        </div>
      </Section>
    </>
  );
}

/**
 * What the card says while a reading is in flight.
 *
 * One phrase at a time, each naming a real part of the work, because the wait is
 * long enough to need saying: several seconds, on every new message. A spinner
 * alone would be honest about the delay and silent about the reason.
 */
const READING_LABELS = [
  'Reading the conversation…',
  'Weighing it against the role…',
  'Taking your notes into account…',
  'Working out what to suggest…',
];

/** How long each phrase holds. The card's tempo, declared once. */
const PHRASE_MS = 1800;
/** How long a phrase takes to leave. Matches `waitingOut` in the stylesheet. */
const PHRASE_OUT_MS = 320;

/**
 * The card with a reading in flight: its name, its heading, and the waiting
 * line. Nothing else.
 *
 * The Actions section and everything the last reading put in it are gone until
 * this one lands. A stale recommendation sitting under a live account is worse
 * than a gap — it invites a press against reasoning that has already been
 * withdrawn.
 */
function Reading() {
  const [step, setStep] = useState(0);
  // The phrase on its way out, held for exactly one transition. Null the rest of
  // the time, so nothing is in the track but the current line — the leaving one
  // is out of flow, and leaving it mounted would keep a second line clipped at
  // the edge of a one-line window.
  const [leaving, setLeaving] = useState<string | null>(null);

  useEffect(() => {
    // Holds on the last phrase rather than looping: a sequence that starts over
    // says the work started over, which is not what happened.
    if (step >= READING_LABELS.length - 1) return;
    const timer = setTimeout(() => {
      setLeaving(READING_LABELS[step] ?? null);
      setStep((n) => n + 1);
    }, PHRASE_MS);
    return () => clearTimeout(timer);
  }, [step]);

  // Cleared on its own clock rather than on animation end: `animationend` never
  // fires under reduced motion, where the rule sets `display: none`, and a
  // phrase that never unmounts would pile up behind the one on screen.
  useEffect(() => {
    if (leaving === null) return;
    const timer = setTimeout(() => setLeaving(null), PHRASE_OUT_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  return (
    <Section title="What I think">
      <p className={styles.waiting}>
        <span className={styles.waitingSpinner} aria-hidden="true">
          <Icon name="spinner" size={15} />
        </span>
        {/*
         * The live region is the track rather than the paragraph, and the
         * leaving phrase is hidden from it: it is on screen for a third of a
         * second on its way out, and announcing "Reading the conversation…" a
         * second time as it leaves would read the sequence back twice over.
         */}
        <span className={styles.waitingTrack} aria-live="polite">
          {leaving !== null && (
            /*
             * Namespaced keys, and the pair of them is the point: keyed on the
             * phrase alone, the span leaving and the span that was current are
             * the same key, so React keeps the DOM node and swaps its class
             * rather than mounting a new one. What it inherits along with the
             * node is the animation it already finished, which is not the one
             * it is now meant to play.
             */
            <span key={`out-${leaving}`} className={styles.waitingLeaving} aria-hidden="true">
              {leaving}
            </span>
          )}
          <span key={`in-${step}`} className={styles.waitingCurrent}>
            {READING_LABELS[step]}
          </span>
        </span>
      </p>
    </Section>
  );
}

/*
 * Whether there is any motion to wait on. Asked at the moment it matters rather
 * than subscribed to: a reading lasts seconds, and somebody who changes this
 * setting mid-reading can have the next one.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

/**
 * The assistant's card in the thread footer — the second version of the footer,
 * shown in place of Quick Replies when the assistant is on for this employer.
 *
 * Shut, it previews the recommended draft (or the account when there is none).
 * Open, it lists the reply group, the actions, then the account, cut to two lines.
 *
 * Collapse is owned by the caller rather than held here, because it resets when
 * the employer opens a different conversation — a decision about one chat, not
 * about the card.
 */
export function AssistantCard({
  expanded,
  onExpandedChange,
  onShortcut,
  assisted = true,
  reading = false,
  flagged = false,
  disabled = false,
  suggest = null,
  account,
  preview,
  children,
  compact,
}: AssistantCardProps) {
  // Only a decision earns the dot. A recommended message is the assistant being
  // helpful; a decision is the assistant asking for the employer's attention,
  // which is the whole of what red means. `recommendsDecision` upstream is the
  // same rule — see docs/ASSISTANT_SCORING.md.
  const decided = suggest !== null && suggest !== 'message';
  /* Compact draws one row, and this is it. Read before the key layer because the
     layer answers from it: what a key may take and what is on screen are the
     same question asked twice. */
  const step = compact ? compactStep(suggest, compact.jobBounded, compact.status) : null;
  // Collapsed, the rows the keys name are not on screen, so the layer sleeps.
  // Nor while a reading is in flight or the card is inert: these shortcuts are
  // bound to the window, so they outlive the rows they stand for, and ⇧1 must
  // not shortlist somebody off a list the card has just taken away.
  useAssistantShortcuts({
    active:
      expanded && !reading && !disabled && (compact !== undefined || onShortcut !== undefined),
    onKey: (digit) => {
      if (!compact) {
        onShortcut?.(digit);
        return;
      }
      /* COMPACT DRAWS ONE ACTION ROW, so ⇧1-4 answers for that row and for
         nothing else. Falling through to the numbering here would let ⇧1
         shortlist somebody off a card that recommended ending the chat — the
         key is on screen, the row it names is not. */
      if (digit <= CARD_ACTIONS.length) {
        if (step && CARD_ACTIONS[digit - 1] === step.action) compact.onTake(step.action);
        return;
      }
      const index = digit - FIRST_DRAFT_SHORTCUT;
      const draft = compact.replies[index];
      if (draft !== undefined) compact.onPick(draft, index);
    },
  });

  /*
   * A new reading returns the card to its compact shape.
   *
   * Expansion is a peek at the list, not a mode. A re-read fires because
   * something changed — a message sent, a note saved, a stage marked — and the
   * expanded list it was annotating is stale the moment that happens. Collapsing
   * as one starts is also what guarantees the next reading lands shut, which is
   * the whole height argument for this design.
   *
   * Held in a ref so the card only asks its owner to shut when `reading` itself
   * changes; the caller hands over a fresh closure on every render, and watching
   * it would collapse the card on every commit.
   */
  const askToShut = useRef(onExpandedChange);
  askToShut.current = onExpandedChange;
  useEffect(() => {
    if (reading) askToShut.current(false);
  }, [reading]);

  /*
   * THE RING ENDS AT ITS OWN LOOP BOUNDARY, NOT WHEN THE ANSWER LANDS.
   *
   * Dropping the class the instant the reading finished cut the ring off
   * wherever it happened to be: a band of colour halfway down one edge, gone
   * between two frames. That is not the reading ending, it is the reading being
   * interrupted, and it is the one moment in the sequence that should read as
   * completion.
   *
   * So `reading` starts it and the animation's own loop boundary ends it. The
   * content does not wait: the account, the rows and the drafts arrive on time
   * and the ring is still travelling behind them for up to one more turn, which
   * is the part worth having. The card is finished, and the last of the light is
   * still leaving it.
   *
   * THREE PHASES, NOT TWO, and the third is the reason for the first. `on` runs
   * to the boundary, `fading` is the colour going out while the sweep carries on
   * turning underneath, `off` is the class gone. A hard cut at the boundary
   * would be the same interruption arriving one turn later. The light has to
   * end, not stop.
   *
   * ASKED OF THE ANIMATIONS RATHER THAN TIMED AGAINST THEM. Timers against
   * constants mirroring the stylesheet's `--phrase` drift the moment a duration
   * changes in CSS: the timer starts firing before the loop boundary, cutting
   * the ring off mid-edge, which is precisely what this exists to stop, and
   * nothing would fail a build over it. The events carry the durations with
   * them.
   *
   * The one case with no event is reduced motion, where `.thinking` has no
   * animation and so no boundary to report. There, with nothing turning, there
   * is no turn to finish.
   */
  const [sweep, setSweep] = useState<'off' | 'on' | 'fading'>(reading ? 'on' : 'off');
  const endSweep = () => setSweep((phase) => (phase === 'on' ? 'fading' : phase));

  useEffect(() => {
    if (reading) setSweep('on');
    else if (prefersReducedMotion()) endSweep();
  }, [reading]);

  /*
   * THE WIDGET'S SHAPE: shut names the move, open offers it, the page holds the
   * rest.
   *
   * Everything the page's card draws is still reachable — through the link at
   * the foot, not by cutting five rows to a width too narrow to read them. The
   * account is the clearest case: it is the card's whole argument, and two lines
   * of it in a 400px column is a sentence, not a reason.
   *
   * Below the hooks, so the card keeps one set of them whichever shape it takes.
   */
  if (compact) {
    const hint = reading
      ? 'Reading…'
      : step
        ? `Quick Action: ${step.look.label}`
        : compact.replies.length > 0
          ? 'Quick Action: Reply'
          : '';

    return (
      <div className={styles.wrap}>
        <div className={`${styles.card} ${styles.compact}`}>
          <button
            className={styles.compactHeader}
            aria-expanded={expanded}
            onClick={() => onExpandedChange(!expanded)}
          >
            {/* Unassisted the card is the employer's own controls over a set of
                openers: no bot mark, no Beta, and "Quick Actions" — the page's
                rule, which the compact shape used to ignore (AI-3092). */}
            {assisted && <BotIcon size={15} />}
            <span className={styles.compactName}>{assisted ? 'Assistant' : 'Quick Actions'}</span>
            {assisted && <span className={styles.beta}>Beta</span>}
            {/* The same verdict the page's header draws, on the same two terms:
                the stored flag stands throughout, the live mark waits for the
                reading to land. Nothing to flag with no reading behind it. */}
            {assisted && (flagged || (!reading && step !== null)) && (
              <span
                className={styles.flag}
                role="img"
                aria-label="Assistant has a suggestion"
                title="Assistant has a suggestion"
              />
            )}
            {/* Shut only. Open, the rows underneath say it better than a line of
                copy naming one of them. */}
            <span className={styles.hint}>{expanded ? '' : hint}</span>
            <span className={expanded ? styles.chevron : styles.chevronCollapsed}>
              <Icon name="chevronUp" size={18} />
            </span>
          </button>
          {expanded && (
            <div className={styles.compactBody}>
              {/* Gone while a reading is in flight, for the reason the page's
                  Actions section is: rows that look pressable, are not, and say
                  nothing about which. */}
              {!reading && compact.replies.length > 0 && (
                <div className={styles.replyGroup}>
                  <SendRow onWriteOwn={compact.onWriteOwn} arrivalIndex={0} disabled={disabled} />
                  <Drafts
                    replies={compact.replies}
                    recommended={compact.recommended}
                    translation={compact.translation}
                    disabled={disabled}
                    onPick={compact.onPick}
                    firstArrival={1}
                  />
                </div>
              )}
              {!reading && step && (
                <>
                  <p className={styles.legend}>Recommended action</p>
                  <Row
                    action={step.action}
                    jobBounded={compact.jobBounded}
                    status={compact.status}
                    onTake={compact.onTake}
                    suggested
                    disabled={disabled}
                    arrivalIndex={compact.replies.length + 1}
                  />
                </>
              )}
              <button className={styles.opens} onClick={compact.onOpenPage}>
                <span>See full analysis and all actions</span>
                <Icon name="arrowUpRight" size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Shut, the reply preview stands in for the account when there is one.
  const shutPreview =
    reading || preview == null ? null : <div className={styles.section}>{preview}</div>;
  const showAccount = expanded || shutPreview === null;
  const body = expanded || shutPreview !== null || account != null;

  return (
    <div className={styles.wrap}>
      <div
        className={[
          styles.card,
          sweep !== 'off' && styles.thinking,
          sweep === 'fading' && styles.thinkingFading,
        ]
          .filter(Boolean)
          .join(' ')}
        /* Filtered to events raised by the card itself. The waiting spinner and
           the draft skeletons are infinite animations inside it and their
           iterations bubble here too, and any one of them would end the sweep at
           an arbitrary angle: the bug this exists to fix, arriving by the back
           door. */
        onAnimationIteration={(event) => {
          if (event.target !== event.currentTarget || reading) return;
          endSweep();
        }}
        /* The far end of the crossfade. `border-color` is the ring's own
           property, and the guard on `fading` is what keeps the arriving
           transition, which runs the same property the other way, from ending
           the sweep the moment it starts. */
        onTransitionEnd={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.propertyName !== 'border-color' || sweep !== 'fading') return;
          setSweep('off');
        }}
      >
        <button
          className={styles.header}
          aria-expanded={expanded}
          onClick={() => onExpandedChange(!expanded)}
        >
          <span className={styles.label}>
            {assisted && <BotIcon size={15} />}
            {/*
             * "Quick Actions" unassisted, not "Actions", and the qualifier is
             * doing real work: it was the same word as the heading over the only
             * list inside, so the card introduced itself and then introduced
             * itself again. Naming the card for the speed it offers rather than
             * for its contents also leaves "Actions" free to go on meaning the
             * list — which is what it means under a header that says
             * "Assistant".
             */}
            {assisted ? 'Assistant' : 'Quick Actions'}
            {/* Says what it is before it says anything wrong. Only where a model
                is behind it: with none, the card is a list of the employer's own
                controls, and there is nothing in beta about those. */}
            {assisted && <span className={styles.beta}>Beta</span>}
            {/* The verdict, drawn here and nowhere else: the assistant has a
                suggestion worth this employer's attention. The inbox row carried
                the same dot until channel custom data turned out to reach the
                candidate as well as the employer.

                Gated on `reading` for the live half, and for the same reason the
                moves are: it comes from the account on screen, and a re-read
                fires precisely because something changed that can overturn it.
                The stored flag stays lit throughout — it is the judgement the
                row is still wearing, and it stands until a landed reading
                replaces it. */}
            {assisted && (flagged || (!reading && decided)) && (
              <span
                className={styles.flag}
                role="img"
                aria-label="Assistant has a suggestion"
                title="Assistant has a suggestion"
              />
            )}
          </span>
          <span className={expanded ? styles.chevron : styles.chevronCollapsed}>
            <Icon name="chevronUp" size={18} />
          </span>
        </button>
        {body && (
          <div className={styles.body}>
            {/* Not there at all while a reading is in flight: a stale answer
                invites a press against reasoning that has been withdrawn. */}
            {expanded ? !reading && children : shutPreview}
            {showAccount && account}
          </div>
        )}
      </div>
    </div>
  );
}

AssistantCard.Section = Section;
AssistantCard.Intro = Intro;
AssistantCard.Moves = Moves;
AssistantCard.Preview = Preview;
AssistantCard.Reading = Reading;
